import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import { BiMenu } from "react-icons/bi";
import { FaUserCircle, FaSignOutAlt, FaExternalLinkAlt, FaCrown } from "react-icons/fa";
import { Drawer } from "@mui/material";
import NavBar from "./NavBar";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const pathname = location.pathname?.split("/")?.[2] || "Dashboard";

  const formatTitle = (path) => {
    switch (path) {
      case "carouselManager":
        return "Carousel Manager";
      case "categoryManager":
        return "Category Manager";
      case "galleryManager":
        return "Gallery Manager";
      case "testimonialManager":
        return "Testimonial Manager";
      case "bookingRequests":
        return "Booking Requests";
      case "pricingManager":
        return "Pricing Manager";
      case "usersManagement":
        return "Users Management";
      case "profile":
        return "My Profile";
      default:
        return "Admin Dashboard";
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-3 shadow-xs sticky top-0 z-40">
      <div className="flex items-center justify-between">
        {/* Left Side: Title / Mobile Toggle */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleDrawerToggle}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Toggle Menu"
          >
            <BiMenu size={24} />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900 capitalize">
                {formatTitle(pathname)}
              </h1>
              <span className="bg-amber-100 text-amber-800 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 uppercase tracking-wider">
                <FaCrown size={10} /> Live API Mode
              </span>
            </div>
            <p className="text-xs text-gray-500 hidden sm:block">
              Tanushree Saha Bridal Studio Management
            </p>
          </div>
        </div>

        {/* Right Side: Quick Links & Profile Info */}
        <div className="flex items-center gap-4">
          {/* Quick link to main website */}
          <a
            href="http://localhost:3000"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-lg transition-all"
          >
            <span>View Website</span>
            <FaExternalLinkAlt size={10} />
          </a>

          {/* Profile User Badge */}
          <div className="flex items-center gap-3 pl-3 border-l border-gray-200">
            <div
              onClick={() => navigate("profile")}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <div className="w-9 h-9 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-sm shadow-sm group-hover:bg-amber-700 transition-colors">
                {user?.name?.[0]?.toUpperCase() || <FaUserCircle size={20} />}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-bold text-gray-800 capitalize group-hover:text-amber-600 transition-colors">
                  {user?.name || "Administrator"}
                </p>
                <p className="text-[0.65rem] text-gray-500 capitalize">
                  {user?.userType || "Admin"}
                </p>
              </div>
            </div>

            {/* Logout button */}
            <button
              onClick={() => {
                if (window.confirm("Are you sure you want to log out?")) {
                  logout();
                  navigate("/login");
                }
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Logout"
            >
              <FaSignOutAlt size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 260,
          },
        }}
      >
        <NavBar />
      </Drawer>
    </header>
  );
}
